import { useEffect } from "react";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "@/lib/site";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Imperatively manages per-route <head> tags (title, meta, canonical,
 * Open Graph, Twitter cards, and JSON-LD structured data). React 19 safe,
 * no extra dependency. The static index.html carries baseline tags for
 * crawlers that do not execute JavaScript.
 */
export default function Seo({
  title,
  description,
  path = "",
  image,
  imageWidth,
  imageHeight,
  imageAlt,
  type = "website",
  jsonLd = null,
  noindex = false,
}) {
  const url = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  // Default brand image is 1536x1024 (australia-water.jpg). Custom images can
  // pass their own dimensions; missing dimensions are skipped cleanly.
  const isDefaultImage = !image || image === DEFAULT_OG_IMAGE;
  const ogImageWidth = imageWidth ?? (isDefaultImage ? 1536 : null);
  const ogImageHeight = imageHeight ?? (isDefaultImage ? 1024 : null);
  const ogImageAlt =
    imageAlt ||
    "Australia rendered in clear flowing water — Pet Water Cremation Australia";
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:type", "image/jpeg");
    upsertMeta("property", "og:image:width", ogImageWidth ? String(ogImageWidth) : null);
    upsertMeta("property", "og:image:height", ogImageHeight ? String(ogImageHeight) : null);
    upsertMeta("property", "og:image:alt", ogImageAlt);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", "en_AU");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);
    upsertMeta("name", "twitter:image:alt", ogImageAlt);

    const id = "route-jsonld";
    let script = document.getElementById(id);
    if (jsonLdStr) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = id;
        document.head.appendChild(script);
      }
      script.textContent = jsonLdStr;
    } else if (script) {
      script.remove();
    }
  }, [title, description, url, ogImage, ogImageWidth, ogImageHeight, ogImageAlt, type, noindex, jsonLdStr]);

  return null;
}
